/**
 * The 30 SENDIG v3.1.1 datasets (domains), offered on the SEND Validator confirm step after the
 * ranked candidates detection found, so any domain can still be chosen as an override. This mirrors
 * the server's `allDomainCodes()` (source of truth for validation); the confirm request is rejected
 * server-side if a code is not in that set, so this list is purely for presentation and must stay in
 * sync with the bundled SENDIG spec.
 */
import { _isNil, _notNil } from '@/littledash';

export interface SendDomain {
  code: string;
  name: string;
}

export const SENDIG_DOMAINS: ReadonlyArray<SendDomain> = [
  { code: 'TA', name: 'Trial Arms' },
  { code: 'TE', name: 'Trial Elements' },
  { code: 'TS', name: 'Trial Summary' },
  { code: 'TX', name: 'Trial Sets' },
  { code: 'DM', name: 'Demographics' },
  { code: 'CO', name: 'Comments' },
  { code: 'SE', name: 'Subject Elements' },
  { code: 'EX', name: 'Exposure' },
  { code: 'DS', name: 'Disposition' },
  { code: 'BW', name: 'Body Weights' },
  { code: 'BG', name: 'Body Weight Gains' },
  { code: 'CL', name: 'Clinical Observations' },
  { code: 'CV', name: 'Cardiovascular Test Results' },
  { code: 'DD', name: 'Death Diagnosis' },
  { code: 'EG', name: 'ECG Test Results' },
  { code: 'FW', name: 'Food and Water Consumption' },
  { code: 'LB', name: 'Laboratory Test Results' },
  { code: 'MA', name: 'Macroscopic Findings' },
  { code: 'MI', name: 'Microscopic Findings' },
  { code: 'OM', name: 'Organ Measurements' },
  { code: 'PC', name: 'Pharmacokinetics Concentrations' },
  { code: 'PP', name: 'Pharmacokinetics Parameters' },
  { code: 'PM', name: 'Palpable Masses' },
  { code: 'RE', name: 'Respiratory Test Results' },
  { code: 'SC', name: 'Subject Characteristics' },
  { code: 'TF', name: 'Tumor Findings' },
  { code: 'VS', name: 'Vital Signs' },
  { code: 'POOLDEF', name: 'Pool Definition' },
  { code: 'RELREC', name: 'Related Records' },
  { code: 'SUPPQUAL', name: 'Supplemental Qualifiers' },
];

/** Human label for a domain code, falling back to the bare code when unknown. */
export const sendDomainLabel = (code: string): string => {
  const match = SENDIG_DOMAINS.find((domain) => domain.code === code);
  return _isNil(match) ? code : `${match.code} — ${match.name}`;
};

/** The SENDIG model every supplemental qualifier file follows, whatever its physical name. */
export const SUPPLEMENTAL_MODEL = 'SUPPQUAL';

const SUPPLEMENTAL_PREFIX = 'SUPP';

const supplementalParentOf = (datasetName: string): SendDomain | undefined =>
  datasetName.startsWith(SUPPLEMENTAL_PREFIX)
    ? SENDIG_DOMAINS.find((domain) => domain.code === datasetName.slice(SUPPLEMENTAL_PREFIX.length))
    : undefined;

/**
 * Human label for a physical dataset: a domain's own label, or for a supplemental qualifier dataset
 * named for its parent (`SUPPMI`) the parent it qualifies. Falls back to the bare name when unknown.
 */
export const sendDatasetLabel = (datasetName: string): string => {
  if (SENDIG_DOMAINS.some((domain) => domain.code === datasetName)) {
    return sendDomainLabel(datasetName);
  }
  const parent = supplementalParentOf(datasetName);
  return _isNil(parent) ? datasetName : `${datasetName} — Supplemental Qualifiers for ${parent.name}`;
};

/**
 * The physical dataset a file result names, such as `SUPPMI`; its domain on a result stored before
 * results named physical datasets.
 */
export const sendDatasetOf = (file: { domain_code: string; dataset_name?: string | null }): string =>
  file.dataset_name ?? file.domain_code;

/**
 * The supplemental qualifier dataset a file would be confirmed as, from its detection: `SUPP` and the
 * parent its RDOMAIN names, or for a file detected as supplemental whose RDOMAIN names no single
 * parent, `SUPPQUAL`. The parent decides even for a file detection could not tell, which names no
 * dataset. Undefined otherwise — a detection that read no values, or another domain's file whose values
 * say nothing about a parent — and the validation run then names it from the file itself.
 */
export const supplementalDatasetOf = (detection: {
  detected_domain: string;
  dataset_name?: string;
  parent_domain?: string;
}): string | undefined => {
  if (_notNil(detection.parent_domain)) {
    return `${SUPPLEMENTAL_PREFIX}${detection.parent_domain}`;
  }
  if (_isNil(detection.dataset_name)) {
    return undefined;
  }
  return detection.detected_domain === SUPPLEMENTAL_MODEL ? SUPPLEMENTAL_MODEL : undefined;
};

/** The label of the supplemental option for a file whose detection names this dataset. */
export const supplementalLabel = (supplementalDataset: string | undefined): string => {
  if (_isNil(supplementalDataset)) {
    return sendDomainLabel(SUPPLEMENTAL_MODEL);
  }
  return supplementalDataset === SUPPLEMENTAL_MODEL
    ? `${SUPPLEMENTAL_MODEL} — Supplemental Qualifiers (no single parent named)`
    : sendDatasetLabel(supplementalDataset);
};
